import type { ActionResult, Language, ModeId, Phase } from "../domain/enums";
import { phaseFromFraction } from "../domain/enums";
import type { Card, Player, Session, SessionEvent } from "../domain/types";
import { buildQueue, nextCardId, pickConsequence } from "./cardQueue";
import { pickHelpers, pickNext } from "./fairCycle";
import { randomSeed } from "./rng";

export function resolveHelperCount(card: Card, totalPlayers: number): number {
  let raw: number | "all" | null | undefined = card.participantsCount;
  if (raw == null) {
    if (card.type === "pair") raw = 1;
    else if (card.type === "group" || card.type === "vote") raw = "all";
    else raw = 0;
  }
  if (raw === "all") return Math.max(0, totalPlayers - 1);
  if (typeof raw !== "number" || !Number.isFinite(raw)) return 0;
  return Math.max(0, Math.min(Math.floor(raw), totalPlayers - 1));
}

export function createSession(args: {
  modeId: ModeId;
  packId: string;
  language: Language;
  players: Player[];
  packCards: Card[];
  seed?: number;
}): Session {
  const seed = args.seed ?? randomSeed();
  const queue = buildQueue({
    cards: args.packCards,
    packId: args.packId,
    language: args.language,
    seed,
  });
  const totalCards = queue.length;

  const playerIds = args.players.map((p) => p.id);
  const firstPick = pickNext({
    allIds: playerIds,
    remainingIds: playerIds,
    seedStream: seed,
    lastPickedId: null,
  });

  const firstCardId = nextCardId(queue, new Set());

  const playerStats: Session["playerStats"] = {};
  for (const p of args.players) {
    playerStats[p.id] = {
      timesSelected: 0,
      done: 0,
      failed: 0,
      skipped: 0,
      replaced: 0,
      timesAsHelper: 0,
    };
  }
  if (firstPick.pickedId && playerStats[firstPick.pickedId]) {
    playerStats[firstPick.pickedId]!.timesSelected += 1;
  }

  const firstCard = args.packCards.find((c) => c.id === firstCardId);
  let helperRemainingIds = playerIds;
  let currentHelperIds: string[] = [];
  if (firstCard && firstPick.pickedId) {
    const helperCount = resolveHelperCount(firstCard, args.players.length);
    const hp = pickHelpers({
      allIds: playerIds,
      excludeId: firstPick.pickedId,
      remainingIds: helperRemainingIds,
      count: helperCount,
      seedStream: (seed + 0x517cc1b7) >>> 0,
    });
    currentHelperIds = hp.pickedIds;
    helperRemainingIds = hp.nextRemainingIds;
    for (const hid of hp.pickedIds) {
      if (playerStats[hid]) playerStats[hid]!.timesAsHelper += 1;
    }
  }

  const id = `s_${seed.toString(36)}_${Date.now().toString(36)}`;
  const sess: Session = {
    id,
    modeId: args.modeId,
    packId: args.packId,
    language: args.language,
    seed,
    players: args.players,
    remainingPlayerIds: firstPick.nextRemainingIds,
    currentPlayerId: firstPick.pickedId,
    currentHelperIds,
    helperRemainingIds,
    consumedCardIds: [],
    pendingReplacedCardIds: [],
    consumedConsequenceCardIds: [],
    currentCardId: firstCardId,
    currentPhase: "warmup",
    cardsSeen: 0,
    totalCards,
    startedAt: Date.now(),
    endedAt: null,
    events: [],
    playerStats,
    undoSnapshot: null,
  };
  return sess;
}

export interface ActionApplied {
  next: Session;
  phaseChanged: boolean;
  sessionEnded: boolean;
  consequence?: {
    kind: "reward" | "penalty";
    cardId: string;
    text: string;
    helperIds: string[];
  } | null;
}

export function applyAction(
  sess: Session,
  action: ActionResult,
  packCards: Card[],
  now: number = Date.now()
): ActionApplied {
  if (sess.endedAt != null) {
    return { next: sess, phaseChanged: false, sessionEnded: true };
  }
  if (sess.currentCardId == null || sess.currentPlayerId == null) {
    return { next: sess, phaseChanged: false, sessionEnded: sess.cardsSeen >= sess.totalCards };
  }

  const undoSnapshot: Session = { ...sess, undoSnapshot: null };

  const event: SessionEvent = {
    cardId: sess.currentCardId,
    playerId: sess.currentPlayerId,
    action,
    timestamp: now,
  };

  let consumedCardIds = sess.consumedCardIds;
  let pendingReplacedCardIds = sess.pendingReplacedCardIds ?? [];
  let cardsSeen = sess.cardsSeen;
  let currentPhase = sess.currentPhase;
  const lastPickedId = sess.currentPlayerId;
  let currentPlayerId = sess.currentPlayerId;
  let remainingPlayerIds = sess.remainingPlayerIds;
  let helperRemainingIds = sess.helperRemainingIds ?? sess.players.map((p) => p.id);
  let seedStream = sess.seed + sess.events.length + 1;

  const newStats = { ...sess.playerStats };
  const myStats = newStats[currentPlayerId] ?? {
    timesSelected: 0,
    done: 0,
    failed: 0,
    skipped: 0,
    replaced: 0,
    timesAsHelper: 0,
  };
  newStats[currentPlayerId] = { ...myStats };

  const justPlayedCard = packCards.find((c) => c.id === sess.currentCardId);
  switch (action) {
    case "done":
    case "failed": {
      consumedCardIds = [...consumedCardIds, sess.currentCardId];
      cardsSeen += 1;
      pendingReplacedCardIds = [];
      newStats[currentPlayerId]![action] = (newStats[currentPlayerId]![action] ?? 0) + 1;
      break;
    }
    case "skipped": {
      pendingReplacedCardIds = [];
      newStats[currentPlayerId]![action] = (newStats[currentPlayerId]![action] ?? 0) + 1;
      break;
    }
    case "replaced": {
      if (!pendingReplacedCardIds.includes(sess.currentCardId)) {
        pendingReplacedCardIds = [...pendingReplacedCardIds, sess.currentCardId];
      }
      newStats[currentPlayerId]![action] = (newStats[currentPlayerId]![action] ?? 0) + 1;
      break;
    }
  }

  let phaseChanged = false;
  if (action === "done" || action === "failed") {
    const fraction = sess.totalCards > 0 ? cardsSeen / sess.totalCards : 0;
    const newPhase = phaseFromFraction(fraction);
    if (newPhase !== currentPhase) {
      phaseChanged = true;
      currentPhase = newPhase;
    }
  }

  const sessionEnded = cardsSeen >= sess.totalCards;

  const consumedSet = new Set(consumedCardIds);
  const exclude =
    action === "replaced" ? new Set(pendingReplacedCardIds) : new Set<string>();
  const queue = buildQueue({
    cards: packCards,
    packId: sess.packId,
    language: sess.language,
    seed: sess.seed,
  });
  const nextCard = sessionEnded ? null : nextCardId(queue, consumedSet, exclude);
  if (
    action === "replaced" &&
    nextCard != null &&
    pendingReplacedCardIds.includes(nextCard)
  ) {
    pendingReplacedCardIds = [nextCard];
  }

  if (!sessionEnded && action !== "replaced") {
    const playerIds = sess.players.map((p) => p.id);
    const pick = pickNext({
      allIds: playerIds,
      remainingIds: remainingPlayerIds,
      seedStream,
      lastPickedId,
    });
    currentPlayerId = pick.pickedId;
    remainingPlayerIds = pick.nextRemainingIds;
    seedStream = pick.nextSeedStream;
    const psPrev = newStats[currentPlayerId] ?? {
      timesSelected: 0,
      done: 0,
      failed: 0,
      skipped: 0,
      replaced: 0,
      timesAsHelper: 0,
    };
    newStats[currentPlayerId] = {
      ...psPrev,
      timesSelected: psPrev.timesSelected + 1,
    };
  }

  let currentHelperIds: string[] = [];
  if (!sessionEnded && nextCard != null) {
    const nextCardObj = packCards.find((c) => c.id === nextCard);
    if (nextCardObj) {
      const helperCount = resolveHelperCount(nextCardObj, sess.players.length);
      const allPlayerIds = sess.players.map((p) => p.id);
      const hp = pickHelpers({
        allIds: allPlayerIds,
        excludeId: currentPlayerId,
        remainingIds: helperRemainingIds,
        count: helperCount,
        seedStream: (seedStream ^ 0x517cc1b7) >>> 0,
      });
      currentHelperIds = hp.pickedIds;
      helperRemainingIds = hp.nextRemainingIds;
      for (const hid of hp.pickedIds) {
        const ps = newStats[hid] ?? {
          timesSelected: 0,
          done: 0,
          failed: 0,
          skipped: 0,
          replaced: 0,
          timesAsHelper: 0,
        };
        newStats[hid] = { ...ps, timesAsHelper: ps.timesAsHelper + 1 };
      }
    }
  }

  let consequence: ActionApplied["consequence"] = null;
  let consumedConsequenceCardIds = sess.consumedConsequenceCardIds ?? [];
  // Per-side suppress: an author can mark a card as "no reward ever" or
  // "no penalty ever" — even if triggersConsequence is on for the other side.
  const sideSuppressed =
    action === "done"
      ? !!justPlayedCard?.suppressReward
      : action === "failed"
        ? !!justPlayedCard?.suppressPenalty
        : false;
  if (
    (action === "done" || action === "failed") &&
    justPlayedCard?.triggersConsequence &&
    !sideSuppressed
  ) {
    const consequenceKind: "reward" | "penalty" =
      action === "done" ? "reward" : "penalty";
    const parentMainBaseId = justPlayedCard
      ? justPlayedCard.id.replace(/^(uk|ru|en):/, "")
      : null;
    const card = pickConsequence({
      cards: packCards,
      modeId: sess.modeId,
      language: sess.language,
      kind: consequenceKind,
      seedStream: (sess.seed + sess.events.length * 0xc2b2ae35) >>> 0,
      phase: currentPhase,
      excludeIds: new Set(consumedConsequenceCardIds),
      parentMainBaseId,
    });
    if (card) {
      const helperCount = resolveHelperCount(card, sess.players.length);
      let conseqHelperIds: string[] = [];
      if (helperCount > 0 && lastPickedId) {
        const allPlayerIds = sess.players.map((p) => p.id);
        const hp = pickHelpers({
          allIds: allPlayerIds,
          excludeId: lastPickedId,
          remainingIds: helperRemainingIds,
          count: helperCount,
          seedStream: (seedStream ^ 0x9e3779b9) >>> 0,
        });
        conseqHelperIds = hp.pickedIds;
        helperRemainingIds = hp.nextRemainingIds;
        for (const hid of hp.pickedIds) {
          const ps = newStats[hid] ?? {
            timesSelected: 0, done: 0, failed: 0, skipped: 0, replaced: 0, timesAsHelper: 0,
          };
          newStats[hid] = { ...ps, timesAsHelper: ps.timesAsHelper + 1 };
        }
      }
      consumedConsequenceCardIds = [...consumedConsequenceCardIds, card.id];
      consequence = {
        kind: consequenceKind,
        cardId: card.id,
        text: card.text,
        helperIds: conseqHelperIds,
      };
    }
  }

  const next: Session = {
    ...sess,
    consumedCardIds,
    pendingReplacedCardIds,
    cardsSeen,
    currentCardId: sessionEnded ? sess.currentCardId : nextCard,
    currentPhase,
    currentPlayerId: sessionEnded ? sess.currentPlayerId : currentPlayerId,
    currentHelperIds: sessionEnded ? sess.currentHelperIds : currentHelperIds,
    helperRemainingIds,
    remainingPlayerIds,
    events: [...sess.events, event],
    playerStats: newStats,
    endedAt: sessionEnded ? now : null,
    undoSnapshot,
    consumedConsequenceCardIds,
  };
  return { next, phaseChanged, sessionEnded, consequence };
}

export function undoLastAction(sess: Session): Session | null {
  if (!sess.undoSnapshot) return null;
  return { ...sess.undoSnapshot };
}

export function progressFraction(sess: Session): number {
  return sess.totalCards > 0 ? sess.cardsSeen / sess.totalCards : 0;
}

export function currentPhaseFor(sess: Session): Phase {
  return phaseFromFraction(progressFraction(sess));
}
